import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Save, Lightbulb, Target, Users2, PackageCheck, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
    { key: 'situation_depart', label: 'Situation de départ', icon: Lightbulb, placeholder: "Contexte initial, problématique observée, données chiffrées de référence avant le projet…" },
    { key: 'cible', label: 'Cible / objectifs', icon: Target, placeholder: "Bénéficiaires visés, état souhaité, indicateurs de succès quantifiables (KPIs SMART)…" },
    { key: 'sponsors', label: 'Sponsors & gouvernance', icon: Users2, placeholder: "Sponsors exécutifs, comité de pilotage, instances de décision et fréquence…" },
    { key: 'livrables', label: 'Livrables attendus', icon: PackageCheck, placeholder: "Liste des livrables (rapports, infrastructures, formations…) avec dates jalons…" },
    { key: 'contraintes', label: 'Contraintes & risques', icon: AlertTriangle, placeholder: "Contraintes budgétaires, calendaires, réglementaires. Risques majeurs identifiés…" },
];

const empty = SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: '' }), {});

export default function ProjectDetails({ projectId }) {
    const { session, profile } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [details, setDetails] = useState(empty);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const isAdmin = !!currentWorkspace?.is_admin || profile?.is_super_admin;

    const authHeader = useCallback(() => ({
        headers: { Authorization: `Bearer ${session?.access_token}` },
    }), [session]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/projects/${projectId}/details`, authHeader());
            setDetails({ ...empty, ...(r.data.details || {}) });
            setDirty(false);
        } catch (e) {
            toast.error('Chargement échoué');
        } finally {
            setLoading(false);
        }
    }, [projectId, authHeader]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/projects/${projectId}/details`, details, authHeader());
            setDirty(false);
            toast.success('Détails enregistrés');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Enregistrement échoué');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="py-10 text-center text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14} /> Chargement…</div>;

    return (
        <div className="space-y-6" data-testid="project-details">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-heading text-xl">Détail du projet</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        Carte d'identité narrative — intégrée aux exports PDF et synthèses COMEX.
                    </p>
                </div>
                {isAdmin && (
                    <Button
                        onClick={save}
                        disabled={!dirty || saving}
                        className="bg-primary hover:bg-primary-hover"
                        data-testid="details-save"
                    >
                        {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
                        Enregistrer
                    </Button>
                )}
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
                {SECTIONS.map(s => {
                    const Icon = s.icon;
                    return (
                        <div key={s.key} className="card-soft p-5" data-testid={`details-section-${s.key}`}>
                            <div className="flex items-center gap-2 mb-3">
                                <div className="h-8 w-8 rounded-lg bg-secondary-light text-secondary flex items-center justify-center">
                                    <Icon size={14} strokeWidth={1.7} />
                                </div>
                                <Label htmlFor={`details-${s.key}`} className="font-heading text-base">{s.label}</Label>
                            </div>
                            <Textarea
                                id={`details-${s.key}`}
                                rows={6}
                                value={details[s.key] || ''}
                                onChange={(e) => { setDetails({ ...details, [s.key]: e.target.value }); setDirty(true); }}
                                placeholder={s.placeholder}
                                disabled={!isAdmin}
                                className="text-sm leading-relaxed"
                                data-testid={`details-input-${s.key}`}
                            />
                        </div>
                    );
                })}
            </div>

            {!isAdmin && (
                <p className="text-xs text-muted-foreground italic">
                    Seuls les administrateurs de l'espace peuvent modifier ces sections.
                </p>
            )}
        </div>
    );
}
